

import api from "@/services/api"
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { BModal } from "bootstrap-vue"
import { Update } from "@/types/update"
import { Getter, Mutation, Action, State } from "vuex-class"
import VuePerfectScrollBar from "vue-perfect-scrollbar"
import { Team } from "@/types"
import UserImage from "@/components/UserImage.vue"
import { UserWithFields, CurrentUser } from "@/types/user"
import SkeletonLoader from "@/components/SkeletonLoader.vue"
import VueSelect from "vue-select"

@Component({
	components: {
		"b-modal": BModal,
		"scrollbar": VuePerfectScrollBar,
		"user-image": UserImage,
		"loader": SkeletonLoader,
		"v-select": VueSelect
	}
})
export default class UpdatesViewer extends Vue {
	@Prop({ default: false })
	public includeAcknowledged!: boolean;

	@Prop({ default: false })
	public value!: boolean;

	@Prop({ default: null })
	public displayFixed!: Update | null;

	@Prop({ type: Number || null, default: 50 })
	public updatesPerPage!: number | null

	@Getter("getMe")
	public me!: CurrentUser | null;

	@Getter("getTeamsById")
	public teamsById!: { [key: string]: Team };

	// whether the updates modal is shown - v-modeled to the modal
	public show: boolean = false

	// updates pulled from the last fetch
	private updates: Update[] = []

	// whether the component is currently fetching updates
	private isFetching: boolean = false

	// whether the component is currently performing a background check for updates
	private isBackgroundChecking: boolean = false

	// whether to show the no new updates notification card
	// i.e. no new updates were found on the last manual refresh
	public displayNoNewUpdates: boolean = false

	// whether previously acknowledged updates should be displayed
	public includeAcknowledgedUpdates: boolean = false

	public userHasUnacknowledgedUpdates: boolean = false

	public userLastUpdateAcknowledged: Date | null = null

	perPageOptions = [ 50, 100, 250, 500, "All"	]

	perPage: number | null = this.updatesPerPage

	public currentPage: number | null = null

	@Watch("value", { immediate: true })
	public valueChanged (newValue: boolean) {
		if (newValue) {
			this.initiateModal()
		}
	}

	@Watch("updatesPerPage", { immediate: true })
	public updatesPerPageChanged (newValue: number | null) {
		this.perPage = newValue
	}

	public initiateModal () {
		this.resetFetchedContent()

		if (this.displayFixed) {
			this.updates.push(this.displayFixed)
			this.isBackgroundChecking = false
			this.show = true
			return
		}

		this.isBackgroundChecking = !this.includeAcknowledged
		this.includeAcknowledgedUpdates = this.includeAcknowledged

		if (!this.isBackgroundChecking) {
			this.show = true
		}

		this.fetchUpdates()
	}

	@Watch("includeAcknowledged")
	public async includeAcknowledgeChanged (newValue: boolean) {
		this.includeAcknowledgedUpdates = newValue
		if (newValue && this.value && this.isBackgroundChecking) {
			// this is the scenario where the user manually opens the updates viewer
			// while it is background checking. Fetching again will handle it.
			// not ideal but a rare case.
			this.isBackgroundChecking = false
			this.show = true
			this.fetchUpdates(false)
		}
	}

	public resetFetchedContent () {
		this.updates = []
		this.displayNoNewUpdates = false
		this.userHasUnacknowledgedUpdates = false
	}

	public async fetchUpdates (assumeRefresh: boolean = true): Promise<void> {
		if (this.displayFixed) {
			// should never occur, just a safeguard
			throw new Error("fetchUpdates cannot be called in a fixed Update display context.")
		}

		let isBackgroundCheck = this.isBackgroundChecking

		this.isFetching = true
		let newUpdates = await api.updatesGet(this.includeAcknowledgedUpdates)

		if (this.updates && this.updates.length === newUpdates.length &&
				newUpdates.length > 0 && newUpdates[0].id === this.updates[0].id) {
			this.displayNoNewUpdates = assumeRefresh
			// background check ran while user had modal open so return if there's no new content
			// if there is new content the content will refresh as normal
			if (this.isBackgroundChecking && this.modalOpen) {
				this.isFetching = false
				return
			}
		} else {
			if (isBackgroundCheck && !this.isBackgroundChecking && this.includeAcknowledged) {
				// updates pulled as part of background check but viewer was manually opened
				// another check is therefore running right behind this one so preserve checking state
				return
			}
			this.updates = newUpdates
			this.displayNoNewUpdates = !this.updates || this.updates.length === 0
		}

		this.currentPage = 1

		if (newUpdates && newUpdates.length > 0) {
			let user = await this.getUserById(this.me!.id)
			this.userLastUpdateAcknowledged = user.lastUpdateAcknowledged ?? null

			this.userHasUnacknowledgedUpdates =
					!this.userLastUpdateAcknowledged || (this.updates[0].lastUpdated > this.userLastUpdateAcknowledged!)

			if ((this.userHasUnacknowledgedUpdates || this.includeAcknowledgedUpdates) && this.modalOpen) {
				this.show = true
			} else if (this.isBackgroundChecking) {
				if (this.userHasUnacknowledgedUpdates && !this.modalOpen) {
					this.show = true
				} else {
					this.isBackgroundChecking = false
					this.emitVisibility(false)
				}
			}
		}

		this.isFetching = false
	}

	public runBackgroundCheck () {
		if (!this.isFetching) {
			this.isBackgroundChecking = true
			this.fetchUpdates()
		}
	}

	public async acknowledgeAllUpdates () {
		if (!this.updates || this.updates.length === 0) {
			return false
		}
		return api.updatesAcknowledge(this.updates[0].id)
	}

	public visibilityDisplayString (teams: string[]) {
		if (!teams || teams.length === 0) {
			return "Everyone"
		}

		let output = ""

		teams.forEach(team => output = output.concat(this.teamsById[team].title + ", "))

		if (teams.length > 0) {
			output = output.slice(0, output.length - 2)
		}

		return output
	}

	public emitVisibility (visibility: boolean) {
		this.$emit("visibility-changed", visibility)
	}

	public modalClosed (acknowledgedUpdates: boolean) {
		if (acknowledgedUpdates && this.userHasUnacknowledgedUpdates) {
			this.acknowledgeAllUpdates()
		}
		this.emitVisibility(false)
	}

	public async getUserById (userId: string): Promise<UserWithFields> {
		return api.userFieldDetails(userId)
	}

	public get okayTitleDisplay (): string {
		return this.userHasUnacknowledgedUpdates ? "Acknowledge All and Close" : "Close"
	}

	public get modalTitle (): string {
		if (this.displayFixed) {
			return "Viewing User Update"
		}

		return this.userHasUnacknowledgedUpdates ? "User Updates - Unread (" + this.unreadUpdates + ")"
			: "User Updates - No New Updates"
	}

	public modifyFixedUpdate () {
		this.show = false
		this.$emit("edit-update", this.displayFixed)
	}

	public loadOlderUpdates () {
		this.resetFetchedContent()
		this.includeAcknowledgedUpdates = true
		this.fetchUpdates()
	}

	public get shouldShowNoUpdatesYetAlert (): boolean {
		return !this.isFetching && (!this.updates || this.updates.length === 0)
	}

	public get disableOkayButton (): boolean {
		return this.userHasUnacknowledgedUpdates && this.isFetching
	}

	public get modalOpen (): boolean {
		return this.show
	}

	public get shouldDisplayLoading (): boolean {
		return this.isFetching && !this.isBackgroundChecking
	}

	public get showOkButton (): boolean {
		return this.userHasUnacknowledgedUpdates && !this.displayFixed
	}

	public isUpdateRead (update: Update) {
		return this.userLastUpdateAcknowledged &&
			update.lastUpdated <= this.userLastUpdateAcknowledged
	}

	public get updatesToDisplay (): Update[] {
		if (!this.isPaginated) {
			return this.updates
		}
		let startIndex: number = (this.currentPage! - 1) * this.perPage!
		return this.updates.slice(startIndex, startIndex + this.perPage!)
	}

	public get isPaginated (): boolean {
		return this.updates && !!this.perPage && this.updates.length > this.perPage
	}

	public get unreadUpdates (): number {
		if (!this.userLastUpdateAcknowledged) {
			return this.updates.length
		}
		return this.updates.filter(update => update.lastUpdated > this.userLastUpdateAcknowledged!).length
	}

	public wasUnreadUpdateSeenBeforeEdit (update: Update): boolean {
		if (!this.userLastUpdateAcknowledged || update.lastUpdated === update.created) {
			return false
		}

		return this.userLastUpdateAcknowledged >= update.created &&
			this.userLastUpdateAcknowledged < update.lastUpdated
	}

	public get userHasNeverAcknowledgedAnUpdate () {
		if (!this.userLastUpdateAcknowledged) {
			return true
		}

		// if the user's value is below a certain date, it's bound to be default
		// this is for safety as sometimes a default date will be 1AD, sometimes it's 1/1/2000
		const defaultUserValue = new Date("2010-01-01T00:00:00Z")

		return new Date(this.userLastUpdateAcknowledged).getTime() <= defaultUserValue.getTime()
	}
}

